import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  MapPin, 
  Building2,
  ArrowLeft,
  Clock,
  Phone,
  Users,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search
} from "lucide-react";

const ClaimVenuePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [availableVenues, setAvailableVenues] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("available");
  const [searchQuery, setSearchQuery] = useState("");
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  
  const [claimData, setClaimData] = useState({
    message: "",
    ownerPhone: ""
  });

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      toast.error("Bu sayfa sadece saha sahipleri için");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [venuesRes, claimsRes] = await Promise.all([
        axios.get(`${API}/venues/available-to-claim`),
        axios.get(`${API}/venues/my-claims`)
      ]);
      setAvailableVenues(venuesRes.data?.venues || []);
      setMyClaims(claimsRes.data?.claims || []);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleClaimVenue = async () => {
    if (!selectedVenue) return;
    
    try {
      const response = await axios.post(`${API}/venues/${selectedVenue.id}/claim`, claimData);
      
      if (response.data.success) {
        toast.success("Talep gönderildi! Admin onayı bekleniyor.");
        setShowClaimDialog(false);
        setSelectedVenue(null);
        setClaimData({ message: "", ownerPhone: "" });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Talep gönderilemedi");
    }
  };

  const getClaimStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-500">Onay Bekliyor</Badge>;
      case "approved":
        return <Badge className="bg-green-500">Onaylandı</Badge>;
      case "rejected":
        return <Badge className="bg-red-500">Reddedildi</Badge>;
      default:
        return null;
    }
  };

  const filteredVenues = availableVenues.filter(venue => 
    venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    venue.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    venue.district?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate("/venue-owner-dashboard")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                <Building2 className="h-7 w-7" />
                Sahalarımı Talep Et
              </h1>
              <p className="text-blue-100 mt-1 text-sm">
                Admin tarafından eklenen sahanızı bulun ve hesabınıza bağlayın
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Bilgi Kartı */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Nasıl Çalışır?</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Aşağıdaki listeden sahanızı bulun</li>
                  <li>"Talep Et" butonuna tıklayın ve bilgilerinizi girin</li>
                  <li>Admin talebinizi inceleyip onaylayacak</li>
                  <li>Onaylandığında saha ve tüm rezervasyonlar hesabınıza bağlanacak</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="available">
              Müsait Sahalar ({availableVenues.length})
            </TabsTrigger>
            <TabsTrigger value="my-claims">
              Taleplerim ({myClaims.length})
            </TabsTrigger>
          </TabsList>

          {/* Müsait Sahalar */}
          <TabsContent value="available">
            {/* Arama */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Saha adı, şehir veya ilçe ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {filteredVenues.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? "Aramanızla eşleşen saha bulunamadı" : "Talep edilebilecek saha yok"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVenues.map((venue) => (
                  <Card key={venue.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{venue.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {venue.city} {venue.district && `/ ${venue.district}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>{venue.format}</span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {venue.capacity} kişi
                        </span>
                        <span className="text-emerald-600 font-medium">{venue.pricePerHour}₺/saat</span>
                      </div>
                      
                      {venue.address && (
                        <p className="text-xs text-gray-500">{venue.address}</p>
                      )}
                      
                      {venue.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-3 w-3" />
                          {venue.phone}
                        </div>
                      )}

                      {venue.availableHours && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="h-3 w-3" />
                          {venue.availableHours}
                        </div>
                      )}
                      
                      {/* Talep Durumu */}
                      {venue.userClaimStatus ? (
                        <div className="pt-2">
                          {getClaimStatusBadge(venue.userClaimStatus)}
                        </div>
                      ) : (
                        <Button 
                          className="w-full mt-2"
                          onClick={() => {
                            setSelectedVenue(venue);
                            setClaimData({ message: "", ownerPhone: user?.phone || "" });
                            setShowClaimDialog(true);
                          }}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Talep Et
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Taleplerim */}
          <TabsContent value="my-claims">
            {myClaims.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Clock className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Henüz talep göndermediniz</p>
                  <Button 
                    className="mt-4"
                    onClick={() => setActiveTab("available")}
                  >
                    Saha Bul ve Talep Et
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myClaims.map((claim) => (
                  <Card 
                    key={claim.id} 
                    className={`border-l-4 ${
                      claim.status === 'approved' ? 'border-l-green-500' :
                      claim.status === 'rejected' ? 'border-l-red-500' :
                      'border-l-yellow-500'
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{claim.venueName}</h3>
                            {getClaimStatusBadge(claim.status)}
                          </div>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {claim.venueCity} {claim.venueDistrict && `/ ${claim.venueDistrict}`}
                          </p>
                          {claim.message && (
                            <p className="text-sm text-gray-600 mt-2">
                              <span className="font-medium">Mesajınız:</span> {claim.message}
                            </p>
                          )}
                          {claim.adminNote && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Admin notu:</span> {claim.adminNote}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            Talep: {new Date(claim.createdAt).toLocaleString('tr-TR')}
                          </p>
                        </div>
                        
                        {claim.status === 'approved' && (
                          <Button onClick={() => navigate("/venue-owner-dashboard")}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Sahama Git
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Talep Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-500" />
              Saha Talep Et
            </DialogTitle>
            <DialogDescription>
              "{selectedVenue?.name}" sahasını talep ediyorsunuz
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-4 rounded-lg text-sm">
              <p className="font-medium mb-2">{selectedVenue?.name}</p>
              <p className="text-gray-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {selectedVenue?.city} {selectedVenue?.district && `/ ${selectedVenue?.district}`}
              </p>
              {selectedVenue?.address && (
                <p className="text-gray-500 mt-1">{selectedVenue?.address}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>İletişim Telefonunuz *</Label>
              <Input
                value={claimData.ownerPhone}
                onChange={(e) => setClaimData({ ...claimData, ownerPhone: e.target.value })}
                placeholder="0532 xxx xx xx"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Mesaj (Opsiyonel)</Label>
              <Textarea
                value={claimData.message}
                onChange={(e) => setClaimData({ ...claimData, message: e.target.value })}
                placeholder="Bu sahanın sahibi olduğunuzu nasıl kanıtlayabilirsiniz? (örn: kira sözleşmesi, tapu vb.)"
                rows={3}
              />
              <p className="text-xs text-gray-500">
                Admin doğrulama için sizinle iletişime geçebilir
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleClaimVenue}>
              <Send className="h-4 w-4 mr-2" />
              Talep Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClaimVenuePage;
